import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { interval, Observable, of, switchMap, takeWhile } from 'rxjs';
import { AppConfigService } from '../config.service';
import { IPaymentGateway } from './payment-gateway.interface';
import { PaymentRequest, PaymentStatusResponse } from '@core/models/payment';
import { SKIP_ERROR_MODAL } from '@core/interceptors/error.interceptor';

@Injectable({ providedIn: 'root' })
export class PaymentGatewayService implements IPaymentGateway {
  private readonly terminalApiHost = 'https://pay.vivenu.dev/api';
  private readonly http = inject(HttpClient);
  private readonly appConfigService = inject(AppConfigService);

  private get gatewayType(): string {
    return this.appConfigService.currentConfig().paymentProvider ?? 'adyen';
  }

  private get host(): string {
    return this.appConfigService.host();
  }

  createPaymentRequest(request: PaymentRequest): Observable<any> {
    return this.http.post(`${this.host}/payments/requests`, request);
  }

  setPaymentProcessor(requestId: string, gatewayId: string): Observable<any> {
    return this.http.post(
      `${this.host}/payments/requests/${requestId}/processor`,
      { paymentGatewayId: gatewayId }
    );
  }

  sendPaymentToTerminal(requestId: string, gatewayId: string, terminalId?: string): Observable<any> {
    const terminal = terminalId ?? this.appConfigService.currentConfig().terminalId;
    return this.http.post<{ id: string; status: string }>(
      `${this.terminalApiHost}/payments/terminals/process`,
      {
        paymentGatewayId: gatewayId,
        paymentRequestId: requestId,
        paymentGatewayType: this.gatewayType,
        terminalId: terminal,
        mode: 'card',
      },
      { context: new HttpContext().set(SKIP_ERROR_MODAL, true) }
    );
  }

  checkPaymentStatus(requestId: string, gatewayId: string, terminalId?: string, processorId?: string): Observable<PaymentStatusResponse> {
    const endpoint = `${this.terminalApiHost}/payments/terminals/process/${processorId}?paymentGatewayId=${gatewayId}&paymentGatewayType=${this.gatewayType}`;
    const verifyProcessor$ = this.http.post<PaymentStatusResponse>(
      `${this.host}/payments/requests/${requestId}/verify`,
      { paymentGatewayId: gatewayId }
    );
    return interval(1000).pipe(
      switchMap(() => this.http.get<PaymentStatusResponse>(endpoint)),
      takeWhile((res) => res.status === 'PENDING', true),
      switchMap((res) => res.status === 'SUCCEEDED' ? verifyProcessor$ : of(res))
    );
  }

  cancelPayment(gatewayId: string, processorId?: string): Observable<any> {
    return this.http.post(
      `${this.terminalApiHost}/payments/terminals/process/${processorId}/cancel?paymentGatewayId=${gatewayId}&paymentGatewayType=${this.gatewayType}`,
      { id: processorId }
    );
  }
}
